import { useQuery } from "@tanstack/react-query";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { apiGet } from "@/lib/api-client";
import { pickNumber, pickString } from "@/lib/format";
import { asRecord } from "@/lib/type-utils";

interface PredictionRow {
  market_id?: string;
  question?: string;
  probability?: number;
  outcome?: string;
  resolved?: boolean;
}

interface PredictionsResponse {
  predictions: PredictionRow[];
  /** Degraded backends return 200 with {predictions: [], error: "..."}. */
  error?: string;
}

export function PredictionsPanel() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["predictions"],
    queryFn: () => apiGet<PredictionsResponse>("/api/predictions"),
  });

  const predictions = data?.predictions ?? [];
  // The backend degrades to HTTP 200 + {predictions: [], error: "..."} when
  // prediction storage is unavailable — that is a failure, not empty history.
  const degraded = Boolean(data?.error);

  return (
    <Panel
      title="Prediction History"
      description="Predictions recorded by the engine (GET /api/predictions)."
    >
      {isLoading ? (
        <LoadingState text="Loading predictions..." />
      ) : isError || degraded ? (
        // A failed or degraded read must never render as "No predictions
        // recorded." — that would present an outage as a clean empty history.
        <QueryErrorState what="prediction history" onRetry={() => void refetch()} />
      ) : predictions.length === 0 ? (
        <p className="py-4 text-center text-sm text-white/40">No predictions recorded.</p>
      ) : (
        <div className="space-y-1.5">
          {predictions.map((prediction, index) => {
            const row = asRecord(prediction);
            const question = pickString(row, ["question", "market_question", "market_id"]);
            // prediction_log rows carry the estimate as predicted_probability
            // (backend/routers/predictions.py serves dict(row) verbatim) —
            // check it first so real history never renders as n/a.
            const probability = pickNumber(row, [
              "predicted_probability",
              "probability",
              "prediction",
              "confidence",
            ]);
            const resolved = Boolean(row.resolved);
            const outcome = pickString(row, ["outcome", "resolution"]);
            return (
              <div
                key={`${pickString(row, ["market_id"]) ?? "prediction"}-${index}`}
                className="flex items-center justify-between rounded border border-white/5 px-3 py-2"
              >
                <p className="max-w-[220px] truncate text-xs text-white/80">{question ?? "Unknown market"}</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-white/80">
                    {probability == null ? "n/a" : `${Math.round(probability * 100)}%`}
                  </span>
                  {resolved ? (
                    <span className="rounded bg-white/10 px-1 py-0.5 text-xs text-white/60">
                      {outcome ?? "resolved"}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
